import { createContextLogger } from './logger';

const logger = createContextLogger('nodeCreationUtils');
import { EdgeObject } from './edgeUtils';
import { Node, useStore } from '../store/store';

// Position interface
export interface Position {
  x: number;
  y: number;
}

// Node data interface
export interface NodeData {
  name: string;
  type: string;
  iconType: string;
  nodeType: string;
  label: string;
  svg: string | null;
}

// Node interface
export interface NodeObject {
  id: string;
  type: string;
  position: Position;
  data: NodeData;
}

// Parameters for createNodeAndMaybeEdge function
export interface CreateNodeAndMaybeEdgeParams {
  type: string; // For icon lookup
  nodeType: string; // For node creation
  name: string;
  icon?: string | null;
  activePipelineId: string;
  activePipelineNodes: NodeObject[];
  currentSelectedNodeId: string | null;
  lastNodePosition: Position | null;
  addNode: (node: NodeObject) => void;
  addEdge: (edge: EdgeObject, skipHistory?: boolean) => void;
  setSelectedNodeId: (nodeId: string) => void;
  saveToHistory?: (description: string) => void; // Optional history saving
  offsetX?: number;
  initialX?: number;
  initialY?: number;
  position?: Position | null; // explicit position for drag-and-drop
  connectEdge?: boolean; // whether to create an edge
  skipEdgeHistory?: boolean; // whether to skip saving edge creation to history
}

// Generate a unique ID for new nodes
const getId = (): string => `node_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

export function createNodeAndMaybeEdge({
  type, // For icon lookup
  nodeType, // For node creation
  name,
  icon,
  activePipelineId,
  activePipelineNodes,
  currentSelectedNodeId,
  lastNodePosition,
  addNode,
  addEdge,
  setSelectedNodeId,
  saveToHistory,
  offsetX = 250,
  initialX = 150,
  initialY = 150,
  position = null, // explicit position for drag-and-drop
  connectEdge = true, // whether to create an edge
  skipEdgeHistory = false, // whether to skip saving edge creation to history
}: CreateNodeAndMaybeEdgeParams): NodeObject | undefined {
  if (!activePipelineId) {
    logger.warn('Cannot create node: No active pipeline');
    return;
  }

  // Determine position logic
  let nodePosition: Position;
  if (position) {
    // Explicit position provided (e.g., from drag-and-drop)
    nodePosition = position;
  } else if (lastNodePosition && typeof lastNodePosition.x === 'number' && (lastNodePosition.x !== 0 || lastNodePosition.y !== 0)) {
    // Calculate new position based on last node position
    // Only if it's not the default {x: 0, y: 0} (which means no node has been placed yet)
    nodePosition = {
      x: lastNodePosition.x + offsetX,
      y: lastNodePosition.y,
    };
  } else {
    // Default fallback position (for first node or when lastNodePosition is {0, 0})
    nodePosition = { x: initialX, y: initialY };
  }

  // Generate unique name to avoid conflicts
  const baseName = name || type || 'Unnamed Node';
  const uniqueName = generateUniqueNodeName(baseName);

  logger.flow.node.debug('Creating node with position:', {
    type,
    nodeType,
    baseName,
    uniqueName,
    position: nodePosition,
    lastNodePosition,
    connectEdge,
  });

  // Create the new node
  const newNode: NodeObject = {
    id: getId(),
    type: nodeType || 'genericStep',
    position: nodePosition,
    data: {
      name: uniqueName,
      type,
      iconType: type,
      nodeType,
      label: uniqueName,
      svg: icon || null,
    },
  };

  logger.flow.node.debug('New node created:', newNode);

  // Save to history BEFORE adding the node
  if (saveToHistory) {
    saveToHistory('Add node');
  }

  // Add the new node to the store
  addNode(newNode);

  // Connect to selected node if requested and a selected node exists
  let edgeCreated = false;
  if (connectEdge && currentSelectedNodeId) {
    const sourceNode = activePipelineNodes.find((n) => n.id === currentSelectedNodeId);

    if (sourceNode) {
      const newEdge: EdgeObject = {
        id: `edge_${currentSelectedNodeId}_${newNode.id}_${Date.now()}`,
        source: currentSelectedNodeId,
        target: newNode.id,
        type: 'custom',
        data: {
          condition: 'unconditional',
          enabled: true,
          fields: [],
        },
      };

      logger.flow.edge.debug('Creating edge:', {
        from: sourceNode.data?.name,
        to: newNode.data.name,
        edgeId: newEdge.id,
      });

      addEdge(newEdge, skipEdgeHistory);
      edgeCreated = true;
    } else {
      logger.warn('Could not find source node for edge creation:', {
        currentSelectedNodeId,
        availableNodes: activePipelineNodes.map((n) => n.id),
      });
    }
  }

  // Set the new node as selected and update position tracking
  setSelectedNodeId(newNode.id);

  return newNode;
}

/**
 * Generate a unique name for a node by checking existing names on the canvas
 * Numbers are based on the same base name/type (e.g., Table Input, Table Input 2, Table Input 3)
 * If the base name exists, finds the next available number in the sequence
 */
export function generateUniqueNodeName(baseName: string, existingNodes?: Node[]): string {
  // Get current nodes from store if not provided or if provided array is empty
  let currentNodes = existingNodes;
  if (!currentNodes || currentNodes.length === 0) {
    const state = useStore.getState();
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    currentNodes = activePipeline?.nodes || [];
    
    logger.debug('Retrieved current nodes from store:', {
      activePipelineId: state.activePipelineId,
      nodeCount: currentNodes.length,
      nodeNames: currentNodes.map(n => n.data.name)
    });
  }

  // Get all existing names 
  const existingNames = currentNodes.map(node => node.data.name);
  const baseNameLower = baseName.toLowerCase();
  
  // If base name doesn't exist (case insensitive), use it as-is
  if (!existingNames.some(name => name.toLowerCase() === baseNameLower)) {
    return baseName;
  }

  // Find all numbers used for this base name
  const usedNumbers = new Set<number>();
  
  existingNames.forEach(name => {
    const nameLower = name.toLowerCase();
    
    // Check if this name starts with our base name
    if (nameLower === baseNameLower) {
      // Exact match = number 1
      usedNumbers.add(1);
    } else if (nameLower.startsWith(baseNameLower + ' ')) {
      // Check if it's "basename number" format
      const suffix = nameLower.substring(baseNameLower.length + 1); // +1 for the space
      const number = parseInt(suffix, 10);
      
      // Only count it if the suffix is purely a number (no extra characters)
      if (!isNaN(number) && suffix === number.toString()) {
        usedNumbers.add(number);
      }
    }
  });

  // Find the next available number (starting from 2 if base name exists)
  let nextNumber = usedNumbers.has(1) ? 2 : 1;
  while (usedNumbers.has(nextNumber)) {
    nextNumber++;
  }

  // If next number is 1, use the base name as-is, otherwise append number
  const uniqueName = nextNumber === 1 ? baseName : `${baseName} ${nextNumber}`;

  logger.debug('Generated unique node name:', {
    baseName,
    uniqueName,
    existingNamesSource: existingNodes ? 'parameter' : 'store',
    existingNames: existingNames.filter(name => 
      name.toLowerCase().startsWith(baseNameLower)
    ),
    usedNumbers: Array.from(usedNumbers).sort((a, b) => a - b),
    nextNumber
  });

  return uniqueName;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Test function to verify unique name generation
 * Can be called from browser console: window.testUniqueNames()
 */
export function testUniqueNameGeneration(): void {
  logger.debug('🧪 Testing unique name generation...');
  
  // Mock existing nodes to test various scenarios
  const mockNodes: Node[] = [
    {
      id: '1',
      type: 'genericStep',
      position: { x: 0, y: 0 },
      data: {
        name: 'Table Input',
        type: 'TableInput',
        iconType: 'TableInput',
        nodeType: 'genericStep',
        label: 'Table Input',
        svg: null,
      }
    },
    {
      id: '2',
      type: 'genericStep',
      position: { x: 0, y: 0 },
      data: {
        name: 'Table Input 2',
        type: 'TableInput',
        iconType: 'TableInput', 
        nodeType: 'genericStep',
        label: 'Table Input 2',
        svg: null,
      }
    },
    {
      id: '3',
      type: 'genericStep',
      position: { x: 0, y: 0 },
      data: {
        name: 'Filter Rows',
        type: 'FilterRows',
        iconType: 'FilterRows',
        nodeType: 'genericStep',
        label: 'Filter Rows',
        svg: null,
      }
    },
    {
      id: '4',
      type: 'genericStep',
      position: { x: 0, y: 0 },
      data: {
        name: 'Table Input 5', // Gap test - 3,4 are missing
        type: 'TableInput',
        iconType: 'TableInput',
        nodeType: 'genericStep',
        label: 'Table Input 5',
        svg: null,
      }
    }
  ];

  // Test cases (based on existing nodes above)
  const testCases = [
    { input: 'Table Input', expected: 'Table Input 3', description: 'Gap filling (1,2,5 exist → 3)' },
    { input: 'Filter Rows', expected: 'Filter Rows 2', description: 'Sequential (1 exists → 2)' },
    { input: 'Sort Rows', expected: 'Sort Rows', description: 'No conflict (first instance)' },
    { input: 'table input', expected: 'table input 3', description: 'Case insensitive gap filling' },
    { input: 'TABLE INPUT', expected: 'TABLE INPUT 3', description: 'Uppercase case insensitive' },
  ];

  logger.debug('📋 Existing nodes:', mockNodes.map(n => n.data.name));

  testCases.forEach(({ input, expected, description }) => {
    const result = generateUniqueNodeName(input, mockNodes);
    const passed = result === expected;
    logger.debug(`${passed ? '✅' : '❌'} "${input}" → "${result}" ${passed ? '' : `(expected "${expected}")`}`);
    logger.debug(`   💡 ${description}`);
    
    // Show debugging info for failed cases
    if (!passed) {
      const baseNameLower = input.toLowerCase();
      const relatedNames = mockNodes
        .map(n => n.data.name)
        .filter(name => name.toLowerCase().startsWith(baseNameLower));
      logger.debug(`   🔍 Related names: ${relatedNames.join(', ')}`);
    }
  });

  logger.debug('🧪 Unique name generation test completed!');
  logger.debug('✅ All tests passed!');
}

// Make functions available globally for testing
if (typeof window !== 'undefined') {
  (window as any).testUniqueNames = testUniqueNameGeneration;
  (window as any).generateUniqueNodeName = generateUniqueNodeName;
}
